import {
	Button,
	Drawer,
	IconButton,
	List,
	Modal,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { X } from "lucide-react";
import { useMemo, useState } from "react";
import {
	type Control,
	type UseFieldArrayAppend,
	useWatch,
} from "react-hook-form";
import type { ExpenseFormModel } from "web_myplann/Finances/Forms/useExpenseForm";
import { ExpenseNamesByType } from "web_myplann/Finances/Helpers/ExpenseTypes";
import {
	ClientFinancialPlanExpenseNameEnum,
	type ClientFinancialPlanExpenseTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannInput } from "web_myplann/Shared/Components/MyPlannInput";

type Props = {
	open: boolean;
	onClose: () => void;
	expenseKey: ClientFinancialPlanExpenseTypeEnum;
	control: Control<ExpenseFormModel>;
	expensesAppend: UseFieldArrayAppend<ExpenseFormModel, "expenses">;
	expensesRemove: (index: number) => void;
};

export const ExpensePicker = ({
	open,
	onClose,
	expenseKey,
	control,
	expensesAppend,
	expensesRemove,
}: Props) => {
	const { t } = useResource();
	const theme = useTheme();

	const expenses = useWatch({ control, name: "expenses" });

	const [selected, setSelected] =
		useState<ClientFinancialPlanExpenseNameEnum | null>(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [value, setValue] = useState<number | undefined>(undefined);
	const [minValue, setMinValue] = useState<number | undefined>(undefined);
	const [hasError, setHasError] = useState(false);

	const availableItems = (ExpenseNamesByType[expenseKey] ?? [])
		.map((name) => ({
			name: name as ClientFinancialPlanExpenseNameEnum,
			label: t(Resources.Finances.Expenses.Names[name]),
		}))
		.filter(
			(item) =>
				!expenses.some(
					(field) => field.type === expenseKey && field.name === item.name,
				),
		);

	const handleChange = (value: typeof selected) => {
		setSelected(value);
		setModalOpen(true);
		setHasError(false);
		setValue(undefined);
		setMinValue(undefined);
		onClose();
	};

	const handleCloseModal = () => {
		setModalOpen(false);
		setHasError(false);
	};

	const handleSave = () => {
		const selectedItem = availableItems.find((item) => item.name === selected);

		if (!selected || !selectedItem || !value) {
			if (!value) {
				setHasError(true);
			}
			return;
		}

		if (selected !== ClientFinancialPlanExpenseNameEnum.Total) {
			const totalExpenseIndex = expenses.findIndex(
				(expense) =>
					expense.type === expenseKey &&
					expense.name === ClientFinancialPlanExpenseNameEnum.Total,
			);
			if (totalExpenseIndex !== -1) {
				expensesRemove(totalExpenseIndex);
			}
		}

		expensesAppend({
			id: null,
			type: expenseKey,
			name: selected,
			monthlyPayment: value,
			sustainableAmount: minValue ?? 0,
		});

		setValue(undefined);
		setMinValue(undefined);
		handleCloseModal();
	};

	const modalTitle = useMemo(() => {
		const selectedItem = availableItems.find((item) => item.name === selected);

		if (selectedItem) {
			return `${t(Resources.Finances.Expenses.Types[expenseKey].title)} - ${selectedItem.label}`;
		}

		return "";
	}, [selected, expenseKey, availableItems, t]);

	return (
		<>
			<Drawer
				anchor="bottom"
				open={open}
				onClose={onClose}
				title={`${t(Resources.Finances.ExpensePicker.title)} - ${t(Resources.Finances.Expenses.Types[expenseKey].title)}`}
				sx={{
					zIndex: (theme) => theme.zIndex.drawer + 1,
				}}
				slotProps={{
					paper: {
						sx: {
							boxShadow: "none",
						},
					},
				}}
			>
				<Stack px={1.6}>
					<Stack pt={2.6} pb={1.2}>
						<Typography
							variant="deprecatedH3"
							color={theme.palette.deprecated.grey[700]}
						>{`${t(Resources.Finances.ExpensePicker.title)} - ${t(Resources.Finances.Expenses.Types[expenseKey].title)}`}</Typography>
					</Stack>

					<List>
						{availableItems.map((item, itemIndex) => (
							<Stack
								key={item.name}
								justifyContent={"center"}
								borderBottom={
									itemIndex !== availableItems.length - 1
										? theme.colors.border.solid(
												theme.palette.deprecated.grey[200],
											)
										: "none"
								}
								onClick={() => handleChange(item.name as typeof selected)}
							>
								<Typography
									fontWeight={600}
									color={theme.palette.deprecated.grey[700]}
									padding={1.6}
								>
									{item.label}
								</Typography>
							</Stack>
						))}
					</List>
				</Stack>
			</Drawer>
			<Modal open={modalOpen} onClose={handleCloseModal}>
				<Stack
					position="absolute"
					top="50%"
					left="50%"
					width="95%"
					sx={{ transform: "translate(-50%, -50%)" }}
				>
					<Stack
						bgcolor={theme.palette.common.white}
						py={2.6}
						px={1.6}
						gap={1.6}
						borderRadius={1.6}
					>
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="center"
						>
							<Typography
								variant="deprecatedH3"
								color={theme.palette.deprecated.grey[700]}
							>
								{modalTitle}
							</Typography>
							<IconButton onClick={handleCloseModal}>
								<X size={theme.iconSize.sm} />
							</IconButton>
						</Stack>

						<MyPlannInput
							label={t(Resources.Finances.ExpensePicker.value)}
							value={value}
							onChange={(e) => {
								setValue(Number(e.target.value));
								if (hasError) {
									setHasError(false);
								}
							}}
							error={hasError}
							helperText={
								hasError ? t(Resources.Form.Validations.required) : ""
							}
							inputProps={{
								inputMode: "numeric",
							}}
							slotProps={{
								input: {
									endAdornment: (
										<Stack direction="row" alignItems="center" gap={0.8}>
											<Typography
												color={theme.palette.deprecated.grey[500]}
												variant="deprecatedBody1"
											>
												{t(Resources.Common.CurrencySign)}
											</Typography>
										</Stack>
									),
								},
							}}
						/>

						<MyPlannInput
							label={t(Resources.Finances.ExpensePicker.minimumValue)}
							value={minValue}
							onChange={(e) => setMinValue(Number(e.target.value))}
							inputProps={{
								inputMode: "numeric",
							}}
							slotProps={{
								input: {
									endAdornment: (
										<Stack direction="row" alignItems="center" gap={0.8}>
											<Typography
												color={theme.palette.deprecated.grey[500]}
												variant="deprecatedBody1"
											>
												{t(Resources.Common.CurrencySign)}
											</Typography>
										</Stack>
									),
								},
							}}
						/>

						<Stack px={1.6}>
							<Typography
								variant="deprecatedCaption"
								color={theme.palette.deprecated.grey[500]}
							>
								{t(Resources.Finances.ExpensePicker.description)}
							</Typography>
						</Stack>

						<Button variant="contained" color="primary" onClick={handleSave}>
							{t(Resources.Common.save)}
						</Button>
					</Stack>
				</Stack>
			</Modal>
		</>
	);
};
